import { z } from 'zod';
export const OrderStatus = z.enum([
    'draft', 'pending_manager', 'pending_price_review', 'active',
    'sent_to_warehouse', 'picking_codes', 'completing_in_1c', 'ready_to_ship',
    'shipped', 'cancelled', 'archived',
]);
export const ORDER_STATUS_LABEL = {
    draft: 'Черновик',
    pending_manager: 'На подтверждении',
    pending_price_review: 'На согласовании цен',
    active: 'Отправлен',
    sent_to_warehouse: 'Обрабатывается в 1С',
    picking_codes: 'Подбор кодов',
    completing_in_1c: 'Завершение заказа в 1С',
    ready_to_ship: 'Готов к отгрузке',
    shipped: 'Отгружен',
    cancelled: 'Отменён',
    archived: 'В архиве',
};
export const OrderLoader = z.enum(['tk_eurasia', 'np_invest']);
export const ORDER_LOADER_LABEL = {
    tk_eurasia: 'ТК Евразия',
    np_invest: 'НП-Инвест',
};
export const OrderItem = z.object({
    id: z.string(),
    position: z.number().int(),
    productId: z.string(),
    productCode: z.string(),
    productName: z.string(),
    unit: z.string(),
    producerName: z.string(),
    temperatureMode: z.string(),
    category: z.string(),
    productGroupId: z.string(),
    productGroupLabel: z.string(),
    qty: z.string(),
    unitPrice: z.string(),
    amount: z.string(),
    isCustomPrice: z.boolean(),
    priceFromTerms: z.string().nullable(),
    customPriceNote: z.string().nullable(),
    needsReview: z.boolean(),
});
export const OrderSummary = z.object({
    id: z.string(),
    tmcNumber: z.string(),
    status: OrderStatus,
    legalEntityId: z.string().nullable(),
    legalEntityName: z.string().nullable(),
    shipmentDate: z.string().nullable(),
    totalAmount: z.string(),
    itemsCount: z.number().int(),
    createdAt: z.string(),
    createdBy: z.string(),
    createdByEmail: z.string(),
    updatedAt: z.string(),
    excelVersion: z.number().int(),
    comment: z.string().nullable(),
    loader: OrderLoader.nullable(),
});
export const OrderPackage = z.object({
    id: z.string(),
    position: z.number().int(),
    weightKg: z.string(),
    lengthCm: z.string(),
    widthCm: z.string(),
    heightCm: z.string(),
});
export const ShipmentPayer = z.enum(['us', 'buyer']);
export const RecipientType = z.enum(['legal', 'individual']);
export const SHIPMENT_FROM_OPTIONS = [
    'ул. Чермянская, 4, стр.1',
    'ш. Коровинское, 22',
];
export const DEFAULT_PACKAGE = {
    weightKg: 8,
    lengthCm: 27,
    widthCm: 39,
    heightCm: 34,
};
export const OrderDetail = OrderSummary.extend({
    cancellationReason: z.string().nullable(),
    shipmentTime: z.string(),
    orderTypeId: z.string().nullable(),
    warehouseId: z.string().nullable(),
    carrierId: z.string().nullable(),
    vehicleId: z.string().nullable(),
    driverId: z.string().nullable(),
    extraServiceId: z.string().nullable(),
    finalizedAt: z.string().nullable(),
    priceReviewedAt: z.string().nullable(),
    priceReviewNote: z.string().nullable(),
    sentToWarehouseAt: z.string().nullable(),
    wasBackToPicking: z.boolean(),
    shippedAt: z.string().nullable(),
    shipmentPayer: ShipmentPayer.nullable(),
    shipmentFrom: z.string().nullable(),
    shipmentToCity: z.string().nullable(),
    shipmentToAddress: z.string().nullable(),
    recipientType: RecipientType.nullable(),
    recipientContact: z.string().nullable(),
    codesFileName: z.string().nullable(),
    codesUploadedAt: z.string().nullable(),
    commentClient: z.string().nullable(),
    managerReturnReason: z.string().nullable(),
    managerReturnedAt: z.string().nullable(),
    clientContactName: z.string().nullable().optional(),
    clientContactPhone: z.string().nullable().optional(),
    clientContactTelegram: z.string().nullable().optional(),
    isClientOrder: z.boolean().optional(),
    items: z.array(OrderItem),
    packages: z.array(OrderPackage),
});
export const PatchOrderHeader = z.object({
    legalEntityId: z.string().uuid().optional(),
    shipmentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    shipmentTime: z.string().optional(),
    orderTypeId: z.string().uuid().optional(),
    warehouseId: z.string().uuid().nullable().optional(),
    carrierId: z.string().uuid().nullable().optional(),
    vehicleId: z.string().uuid().nullable().optional(),
    driverId: z.string().uuid().nullable().optional(),
    extraServiceId: z.string().uuid().nullable().optional(),
    comment: z.string().max(2000).nullable().optional(),
    commentClient: z.string().max(2000).nullable().optional(),
    shipmentPayer: ShipmentPayer.nullable().optional(),
    loader: OrderLoader.nullable().optional(),
    shipmentFrom: z.string().max(500).nullable().optional(),
    shipmentToCity: z.string().max(200).nullable().optional(),
    shipmentToAddress: z.string().max(500).nullable().optional(),
    recipientType: RecipientType.nullable().optional(),
    recipientContact: z.string().max(500).nullable().optional(),
});
// Клиентская проекция заказа: только то, что разрешено видеть клиенту.
// Используется при role=client. Без логистики, кодов, внутренних цен, priceReviewNote.
export const OrderClientItem = z.object({
    id: z.string(),
    position: z.number().int(),
    productCode: z.string(),
    productName: z.string(),
    unit: z.string(),
    qty: z.string(),
    unitPrice: z.string(),
    amount: z.string(),
});
export const OrderClientView = z.object({
    id: z.string(),
    tmcNumber: z.string(),
    status: OrderStatus,
    legalEntityId: z.string().nullable(),
    legalEntityName: z.string().nullable(),
    shipmentDate: z.string().nullable(),
    shipmentTime: z.string(),
    shipmentToCity: z.string().nullable(),
    shipmentToAddress: z.string().nullable(),
    recipientType: RecipientType.nullable(),
    recipientContact: z.string().nullable(),
    totalAmount: z.string(),
    itemsCount: z.number().int(),
    commentClient: z.string().nullable(),
    managerReturnReason: z.string().nullable(),
    managerReturnedAt: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    shippedAt: z.string().nullable(),
    items: z.array(OrderClientItem),
});
export const OrderClientListItem = z.object({
    id: z.string(),
    tmcNumber: z.string(),
    status: OrderStatus,
    shipmentDate: z.string().nullable(),
    totalAmount: z.string(),
    commentClient: z.string().nullable(),
    createdAt: z.string(),
});
export const ReturnToClientRequest = z.object({
    reason: z.string().min(1).max(1000),
});
export const PackageBody = z.object({
    weightKg: z.number().positive(),
    lengthCm: z.number().positive(),
    widthCm: z.number().positive(),
    heightCm: z.number().positive(),
});
export const PatchPackageBody = PackageBody.partial();
export const AddItemRequest = z.object({
    productId: z.string().uuid(),
    qty: z.number().positive(),
    isCustomPrice: z.boolean().optional(),
    customUnitPrice: z.number().positive().optional(),
    customPriceNote: z.string().max(1000).optional(),
});
export const PatchItemRequest = z.object({
    qty: z.number().positive().optional(),
    customPriceNote: z.string().max(1000).optional(),
});
export const RejectPricesRequest = z.object({
    note: z.string().max(1000),
});
export const CancelOrderRequest = z.object({
    reason: z.string().max(1000),
});
// === Notifications ===
export const NotificationItem = z.object({
    id: z.string(),
    kind: z.string(),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
    seenAt: z.string().nullable(),
});
export const NotificationsResponse = z.object({
    items: z.array(NotificationItem),
    unseenCount: z.number().int(),
});
//# sourceMappingURL=orders.js.map